import { PropertiesPanelLeafConfig, PropertiesPanelPropertyConfig } from '../../../../../widgets/propertiesPanel';
import { PBR_RANGE_LIMITS, PBR_SECTIONS, PbrSection } from './pbrMaterialLayout';

export interface PbrPanelSection {
    id: string;
    label: string;
    advanced: boolean;
    groups: PropertiesPanelPropertyConfig[];
}

// SI INDICIZZANO LE FOGLIE, NON I GRUPPI. Il pannello vecchio annida: dentro il gruppo `metallic`
// c'è una proprietà che si chiama anch'essa `metallic`, e dentro `clear-coat-roughness` (gruppo)
// una `clear-coat-roughness` (valore). Indicizzando anche i contenitori, chiedere una riga
// restituirebbe a volte la riga e a volte tutto il ramo che la contiene — cioè un pannello che
// cambia forma a seconda di dove capita un nome. I contenitori si attraversano e basta: qui
// dentro esistono solo le foglie, e i loro nomi sono quelli che la tabella nomina.
const indexLeaves = (properties: PropertiesPanelPropertyConfig[], into: Map<string, PropertiesPanelLeafConfig[]>) => {
    properties.forEach((property) => {
        if (!property) return;
        if (property.type === 'group') {
            indexLeaves(property.properties, into);
            return;
        }
        const found = into.get(property.id);
        if (found) found.push(property);
        else into.set(property.id, [property]);
    });
    return into;
};

// I limiti corretti si applicano alla riga senza toccare chi l'ha costruita: si riscrive la
// copia che finisce nel pannello, non l'originale. `sliderMax` arriva insieme agli altri, e il
// controllo sa già cosa farne — cursore fin lì, campo numerico libero.
const withLimits = (property: PropertiesPanelLeafConfig): PropertiesPanelLeafConfig => {
    const limits = property.type === 'range' ? PBR_RANGE_LIMITS[property.id] : undefined;
    return limits ? { ...property, ...limits } : property;
};

// GLI ID CHE UNO SLOT DI TEXTURE PRODUCE. `createTexturePropertyConfig` ne emette fino a quattro
// con lo stesso prefisso — la mappa, la sua versione mobile, la scala, il canale UV — e sono la
// seconda metà di un canale: il file che lo dipinge, non il valore che governa. È questa
// distinzione a mettere il divisore nel cassetto, e a dire quale anteprima va sulla riga.
const isMapPart = (id: string) => id.endsWith('-image') || id.endsWith('-texture-scaling') || id.endsWith('-uv-channel');

/**
 * Rimonta le proprietà di un materiale PBR nella gerarchia di `PBR_SECTIONS`.
 *
 * Riceve l'elenco piatto che `useMaterialsPropertiesCreator` costruisce già, più le poche righe
 * che quel pannello non ha (`buildPbrExtraProperties`), e restituisce le sezioni. Ciò che la
 * tabella non nomina non finisce nel risultato, ed è esattamente così che si nasconde.
 */
export const buildPbrMaterialPanel = (flat: PropertiesPanelPropertyConfig[], extra: PropertiesPanelPropertyConfig[] = []): PbrPanelSection[] => {
    const leaves = indexLeaves([...flat, ...extra], new Map());

    // UNO STESSO NOME PUÒ VALERE PER PIÙ RIGHE, e va bene: uno slot di texture emette la mappa e
    // la sua versione mobile con lo stesso id. Si prendono tutte, nell'ordine in cui il pannello
    // le costruisce — desktop e poi mobile — che è l'ordine in cui si leggono.
    const take = (ids: string[]) => ids.flatMap((id) => (leaves.get(id) ?? []).map(withLimits));

    return PBR_SECTIONS.map((section: PbrSection) => ({
        id: section.id,
        label: section.label,
        advanced: Boolean(section.advanced),
        groups: section.groups
            .map((group) => {
                // L'INTERRUTTORE DELLA FAMIGLIA STA NELLA TESTATA, non fra le righe. È ciò che decide se
                // il resto vale: in mezzo agli altri parametri sarebbe una riga come le altre in un
                // elenco che invece governa, e per spegnere uno strato bisognerebbe prima aprirlo.
                const [feature] = group.feature ? take([group.feature]) : [];

                const channels = group.channels
                    .map((channel) => {
                        const properties = take(channel.take);
                        if (properties.length === 0) return null;
                        // UN CANALE SENZA NOME NON È UN CANALE: è il corpo del gruppo. Lo usano i
                        // tre gruppi di Rendering, che non hanno cose da toccare ma una manciata
                        // di valori sciolti — e un titolo che ripete quello del gruppo sarebbe una
                        // maniglia in più per arrivare alla stessa riga.
                        if (!channel.label) return properties;

                        // IL CANALE È UNA RIGA quando la tabella dice che cosa resta in vista. Il resto si
                        // divide da sé in due: i valori che il canale governa e il file che lo dipinge.
                        // Dove la tabella non dice niente il canale resta il gruppo di prima — la
                        // decisione è aperta, e un pannello che intanto indovina è un pannello da disfare.
                        if (channel.onRow) {
                            const head = channel.onRow === 'map' ? undefined : properties.find((property) => property.id === channel.onRow);
                            const map = properties.filter((property) => isMapPart(property.id));
                            const values = properties.filter((property) => property !== head && !isMapPart(property.id));

                            return [
                                {
                                    type: 'channel' as const,
                                    id: group.id + '-' + channel.label,
                                    label: channel.label,
                                    head,
                                    properties: values.length ? values : undefined,
                                    map: map.length ? map : undefined
                                }
                            ];
                        }

                        return [
                            {
                                type: 'group' as const,
                                id: group.id + '-' + channel.label,
                                header: channel.label,
                                defaultIsOpen: false,
                                properties
                            }
                        ];
                    })
                    .filter(Boolean)
                    .flat() as PropertiesPanelPropertyConfig[];

                if (!feature && channels.length === 0) return null;

                return {
                    type: 'group' as const,
                    id: group.id,
                    header: group.label,
                    defaultIsOpen: Boolean(group.open),
                    trailing: feature,
                    properties: channels
                };
            })
            .filter(Boolean) as PropertiesPanelPropertyConfig[]
    })).filter((section) => section.groups.length > 0);
};
